import type { FC } from 'react'

import { useState } from 'react'
import { Check, Link2, Mail } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PlatformIcon } from '@/components/kit/platform-icon'

interface ShareButtonsProps {
    title: string
    className?: string
    vertical?: boolean
}

const button = 'flex size-11 items-center justify-center rounded-full border border-ink/12 bg-paper text-ink transition-[background-color,border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-ink hover:bg-ink hover:text-cream'

export const ShareButtons: FC<ShareButtonsProps> = ({ title, className, vertical = false }) => {
    const [copied, setCopied] = useState(false)

    const open = (target: 'x' | 'linkedin') => {
        const url = encodeURIComponent(window.location.href.split('#')[0])
        const text = encodeURIComponent(title)
        const href = target === 'x'
            ? `https://twitter.com/intent/tweet?url=${url}&text=${text}`
            : `https://www.linkedin.com/sharing/share-offsite/?url=${url}`

        window.open(href, '_blank', 'noopener,noreferrer,width=640,height=560')
    }

    const email = () => {
        const url = window.location.href.split('#')[0]

        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n\n${url}`)}`
    }

    const copy = async () => {
        const url = window.location.href.split('#')[0]

        try {
            await navigator.clipboard.writeText(url)
        }
        catch {
            const field = document.createElement('textarea')

            field.value = url
            document.body.appendChild(field)
            field.select()
            document.execCommand('copy')
            field.remove()
        }

        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className={cn('flex gap-2', vertical ? 'flex-col' : 'flex-row flex-wrap', className)}>
            <button
                type='button'
                onClick={copy}
                aria-label={copied ? 'Link copied' : 'Copy link'}
                className={cn(button, copied && 'border-ember bg-ember text-ink hover:border-ember hover:bg-ember hover:text-ink')}
            >
                {copied ? <Check className='size-4' /> : <Link2 className='size-4' />}
            </button>
            <button
                type='button'
                onClick={() => open('x')}
                aria-label='Share on X'
                className={button}
            >
                <PlatformIcon
                    name='x'
                    className='size-4'
                />
            </button>
            <button
                type='button'
                onClick={() => open('linkedin')}
                aria-label='Share on LinkedIn'
                className={button}
            >
                <PlatformIcon
                    name='linkedin'
                    className='size-4'
                />
            </button>
            <button
                type='button'
                onClick={email}
                aria-label='Share by email'
                className={button}
            >
                <Mail className='size-4' />
            </button>
            <span
                role='status'
                aria-live='polite'
                className='sr-only'
            >
                {copied ? 'Link copied to clipboard' : ''}
            </span>
        </div>
    )
}
